import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DropdownOverlay, DropdownPanel, DropdownSurface } from '../dropdown/dropdown';

export type DropdownAnchor = 'left' | 'top' | 'bottom' | 'right';
export type DropdownAlignment = 'left' | 'top' | 'bottom' | 'right';
export type DropdownPlacement = 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' | 'left-start' | 'left-end' | 'right-start' | 'right-end';

interface DropdownPosition {
    x: number;
    y: number;
}

interface DropdownProps {
    buttonRef: HTMLElement;
    placement: DropdownPlacement;
    children?: React.ReactNode;
    detachedTop?: React.ReactNode;
    width?: string;
    minWidth?: string;
    maxWidth?: string;
    maxHeight?: string;
    surfacePadding?: string;
    offset?: number;
    viewportMargin?: number;
    zIndex?: number;
    matchTriggerWidth?: boolean;
    overlay?: boolean;
    overlayBackground?: string;
}

interface CustomDropdownProps extends Omit<DropdownProps, 'buttonRef' | 'placement'> {}

const activeDropdownCloseRef: { current: (() => void) | null } = {
    current: null
};

const isPlacement = (value: DropdownAnchor | DropdownPlacement): value is DropdownPlacement => value.indexOf('-') !== -1;

const resolveLegacyPlacement = (anchor: DropdownAnchor, alignment: DropdownAlignment = 'left'): DropdownPlacement => {
    switch (anchor) {
        case 'top':
            return alignment === 'right' ? 'top-end' : 'top-start';
        case 'bottom':
            return alignment === 'right' ? 'bottom-end' : 'bottom-start';
        case 'left':
            return alignment === 'bottom' ? 'left-end' : 'left-start';
        case 'right':
            return alignment === 'bottom' ? 'right-end' : 'right-start';
    }
};

const getOppositePlacement = (placement: DropdownPlacement): DropdownPlacement => {
    switch (placement) {
        case 'top-start':
            return 'bottom-start';
        case 'top-end':
            return 'bottom-end';
        case 'bottom-start':
            return 'top-start';
        case 'bottom-end':
            return 'top-end';
        case 'left-start':
            return 'right-start';
        case 'left-end':
            return 'right-end';
        case 'right-start':
            return 'left-start';
        case 'right-end':
            return 'left-end';
    }
};

const getPosition = (triggerRect: DOMRect, dropdownRect: DOMRect, placement: DropdownPlacement, offset: number): DropdownPosition => {
    const [side, align] = placement.split('-') as [DropdownAnchor, 'start' | 'end'];

    let x = triggerRect.left;
    let y = triggerRect.top;

    if (side === 'top' || side === 'bottom') {
        y = side === 'top' ? triggerRect.top - dropdownRect.height - offset : triggerRect.bottom + offset;
        x = align === 'end' ? triggerRect.right - dropdownRect.width : triggerRect.left;
    }

    if (side === 'left' || side === 'right') {
        x = side === 'left' ? triggerRect.left - dropdownRect.width - offset : triggerRect.right + offset;
        y = align === 'end' ? triggerRect.bottom - dropdownRect.height : triggerRect.top;
    }

    return { x, y };
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const matchesFocusVisible = (element: Element) => {
    try {
        return element.matches(':focus-visible');
    } catch {
        return false;
    }
};

const Dropdown: FC<DropdownProps> = ({
    buttonRef,
    placement,
    children,
    detachedTop,
    width,
    minWidth,
    maxWidth,
    maxHeight,
    surfacePadding,
    offset = 4,
    viewportMargin = 8,
    zIndex,
    matchTriggerWidth,
    overlay,
    overlayBackground
}) => {
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const [position, setPosition] = useState<DropdownPosition>({ x: 0, y: 0 });
    const [isPositioned, setIsPositioned] = useState(false);

    // KEYBOARD FOCUS (accessibility, 2026-10-22). The surface lives in a portal at the end of the page, so after opening
    // it with the keyboard Tab used to walk the rest of the page before reaching it. Once it is visible the focus moves
    // to its first control, unless something inside (a search box) already took it. Only when the trigger was opened from
    // the keyboard (its focus is :focus-visible): opened with the mouse, nothing moves, as before.
    // On close the focus returns to the trigger only when the menu was dismissed from inside with Esc or Tab. Choosing an
    // item (a click, Enter, Space) never sends it back: the item may open a dialog, and a focused trigger behind it would
    // reopen the menu above the dialog on the next Enter.
    const dismissedFromInside = useRef(false);
    useEffect(() => {
        const surface = dropdownRef.current;
        if (!isPositioned || !surface || surface.contains(document.activeElement)) return;
        const active = document.activeElement;
        if (!active || !buttonRef.contains(active) || !matchesFocusVisible(active)) return;
        surface.querySelector<HTMLElement>(FOCUSABLE)?.focus({ preventScroll: true });
    }, [isPositioned]);
    useEffect(
        () => () => {
            const active = document.activeElement;
            if (!dismissedFromInside.current || (active && active !== document.body && document.contains(active))) return;
            const trigger = buttonRef.matches(FOCUSABLE) ? buttonRef : buttonRef.querySelector<HTMLElement>(FOCUSABLE);
            trigger?.focus({ preventScroll: true });
        },
        [buttonRef]
    );

    const updatePosition = useCallback(() => {
        if (!dropdownRef.current) return;

        const triggerRect = buttonRef.getBoundingClientRect();
        const dropdownRect = dropdownRef.current.getBoundingClientRect();
        const [side] = placement.split('-') as [DropdownAnchor, 'start' | 'end'];

        let currentPlacement = placement;
        let nextPosition = getPosition(triggerRect, dropdownRect, currentPlacement, offset);

        if (
            (side === 'bottom' && nextPosition.y + dropdownRect.height > window.innerHeight - viewportMargin) ||
            (side === 'top' && nextPosition.y < viewportMargin)
        ) {
            currentPlacement = getOppositePlacement(currentPlacement);
            nextPosition = getPosition(triggerRect, dropdownRect, currentPlacement, offset);
        }

        if (
            (side === 'right' && nextPosition.x + dropdownRect.width > window.innerWidth - viewportMargin) ||
            (side === 'left' && nextPosition.x < viewportMargin)
        ) {
            currentPlacement = getOppositePlacement(currentPlacement);
            nextPosition = getPosition(triggerRect, dropdownRect, currentPlacement, offset);
        }

        const maxX = Math.max(viewportMargin, window.innerWidth - dropdownRect.width - viewportMargin);
        const maxY = Math.max(viewportMargin, window.innerHeight - dropdownRect.height - viewportMargin);

        const clampedPosition = {
            x: clamp(nextPosition.x, viewportMargin, maxX),
            y: clamp(nextPosition.y, viewportMargin, maxY)
        };

        setPosition((current) => (current.x === clampedPosition.x && current.y === clampedPosition.y ? current : clampedPosition));
        setIsPositioned(true);
    }, [buttonRef, offset, placement, viewportMargin]);

    useLayoutEffect(() => {
        updatePosition();
    }, [updatePosition]);

    useEffect(() => {
        updatePosition();

        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updatePosition) : null;
        if (resizeObserver) {
            resizeObserver.observe(buttonRef);
            if (dropdownRef.current) {
                resizeObserver.observe(dropdownRef.current);
            }
        }

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
            resizeObserver?.disconnect();
        };
    }, [buttonRef, updatePosition]);

    const resolvedWidth = matchTriggerWidth ? `${buttonRef.getBoundingClientRect().width}px` : width;
    const resolvedZIndex = zIndex ?? 100;

    return createPortal(
        <>
            {overlay && <DropdownOverlay $zIndex={resolvedZIndex - 1} $background={overlayBackground} />}
            <DropdownSurface
                onClick={(e) => e.nativeEvent.stopPropagation()}
                onMouseDown={(e) => e.nativeEvent.stopPropagation()}
                onKeyDown={(e) => {
                    dismissedFromInside.current = e.key === 'Escape' || e.key === 'Tab';
                }}
                ref={dropdownRef}
                x={position.x}
                y={position.y}
                $visible={isPositioned}
                $zIndex={resolvedZIndex}
                $width={resolvedWidth}
                $minWidth={minWidth}
                $maxWidth={maxWidth}
                $maxHeight={maxHeight}
                $padding={surfacePadding}
                $transparent={!!detachedTop}
            >
                {detachedTop}
                {detachedTop ? <DropdownPanel $padding={surfacePadding}>{children}</DropdownPanel> : children}
            </DropdownSurface>
        </>,
        document.body
    );
};

export const useDropdown = () => {
    const [isOpened, setIsOpened] = useState(false);
    const [placement, setPlacement] = useState<DropdownPlacement>('bottom-start');

    const elementRef = useRef<HTMLElement | null>(null);

    const close = useCallback(() => {
        setIsOpened(false);
        elementRef.current = null;

        if (activeDropdownCloseRef.current === close) {
            activeDropdownCloseRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!isOpened) return;

        const onMouseDown = (e: MouseEvent) => {
            if (elementRef.current && elementRef.current.contains(e.target as HTMLElement)) {
                e.stopPropagation();
                return;
            }

            close();
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                // Esc used here: a dialog around the menu must not close on the same key press.
                e.preventDefault();
                close();
            }
        };

        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [close, isOpened]);

    useEffect(() => {
        return () => {
            if (activeDropdownCloseRef.current === close) {
                activeDropdownCloseRef.current = null;
            }
        };
    }, [close]);

    const CustomDropdown: FC<CustomDropdownProps> = ({ children, ...props }) => {
        if (!isOpened || !elementRef.current) return null;

        return (
            <Dropdown buttonRef={elementRef.current} placement={placement} {...props}>
                {children}
            </Dropdown>
        );
    };

    const open = useCallback(
        (el: HTMLElement, anchor: DropdownAnchor | DropdownPlacement = 'bottom', alignment: DropdownAlignment = 'left') => {
            if (activeDropdownCloseRef.current && activeDropdownCloseRef.current !== close) {
                activeDropdownCloseRef.current();
            }

            activeDropdownCloseRef.current = close;
            elementRef.current = el;
            setPlacement(isPlacement(anchor) ? anchor : resolveLegacyPlacement(anchor, alignment));
            setIsOpened(true);
        },
        [close]
    );

    const result = useMemo(() => [open, close, isOpened, CustomDropdown] as const, [close, isOpened, open, placement]);

    return result;
};
